/**
 * JOHeaderShopShortcut — Injects a "Boutique" pill in the header at the
 * 1024–1279 px breakpoint where the desktop nav collapses behind the
 * burger menu. Keeps the shop CTA always visible — critical for sales.
 *
 * Non-invasive : runs in useEffect, mutates DOM only. AdminLayout.jsx /
 * Header.jsx remain untouched (project rule).
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const TARGET_HREF = "/shop";
const MARKER_TESTID = "header-shop-shortcut";

export default function JOHeaderShopShortcut() {
  const location = useLocation();

  useEffect(() => {
    // Don't touch the admin area
    if (location.pathname.startsWith("/admin")) return;

    const inject = () => {
      // Already injected on this page render?
      if (document.querySelector(`a[data-testid="${MARKER_TESTID}"]`)) return true;

      // Skip paysagiste mode entirely — no e-commerce there.
      if (document.body.getAttribute("data-universe") === "paysagiste") return true;

      // Anchor: the cart button in the cockpit header. We insert our
      // "Boutique" shortcut right before it so the shopping CTA is
      // always visible at the tablet-landscape breakpoint (1024-1279)
      // where the desktop nav collapses behind the burger menu.
      const cart =
        document.querySelector('header [data-testid="header-cart-btn"]') ||
        document.querySelector('header a[href="/cart"]');
      if (!cart || !cart.parentElement) return false;

      const a = document.createElement("a");
      a.setAttribute("href", TARGET_HREF);
      a.setAttribute("data-testid", MARKER_TESTID);
      a.setAttribute("aria-label", "Boutique en ligne");
      // Styles match the existing pill buttons in the header
      a.style.cssText = [
        "display:none",                      // hidden by default; CSS @media unhides at 1024-1279
        "align-items:center",
        "gap:6px",
        "height:40px",
        "padding:0 16px",
        "border-radius:9999px",
        "background:var(--jdo-gold, #C99E30)",
        "color:#1B4332",
        "font-weight:800",
        "font-size:13px",
        "letter-spacing:0.4px",
        "text-decoration:none",
        "box-shadow:0 2px 8px rgba(201, 158, 48, 0.35)",
        "transition:transform 0.15s ease, box-shadow 0.15s ease",
        "white-space:nowrap",
      ].join(";");
      a.onmouseenter = () => { a.style.transform = "translateY(-1px)"; a.style.boxShadow = "0 4px 14px rgba(201,158,48,0.5)"; };
      a.onmouseleave = () => { a.style.transform = ""; a.style.boxShadow = "0 2px 8px rgba(201, 158, 48, 0.35)"; };
      // Inline SVG cart icon (Lucide ShoppingCart)
      a.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <circle cx="8" cy="21" r="1"/><circle cx="19" cy="21" r="1"/>
          <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"/>
        </svg>
        <span>Boutique</span>
      `;
      cart.parentElement.insertBefore(a, cart);
      return true;
    };

    if (inject()) return;
    let tries = 0;
    const interval = setInterval(() => {
      tries++;
      if (inject() || tries > 20) clearInterval(interval);
    }, 250);
    return () => clearInterval(interval);
  }, [location.pathname]);

  return null;
}
